import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { MRT_ColumnDef } from 'material-react-table';
import FormBuilder from '../../../../../../shared/components/form-builder/form-builder';
import { useApi } from '../../../../../../shared/hooks/useApi';
import { transformProductData } from '../../../../tc-models/tc-req-dto/tc-component-details.req_dto';
import { Option } from '../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto';
import { useParams } from 'react-router-dom';
import { TCAppConfig } from '../../../../../../shared/app-configs/tc-app-configs';
import { ProductShipmentResponse } from '../../certified-products/certified-products.model';

interface TCComponentInformation {
  tcAssociatedProductNo: string;
  tcProductComponentOrder: string;
  tcProductComponentNo: string;
  tcComponentDetail: string;
  tcComponentInfoTrimsExcluded: string;
  tcComponentInfoAssociatedTc: string;
  tcComponentInfoFiberLength: string;
  tcComponentInfoYarnCount: string;
  tcComponentInfoFabricWeightAndConstruct: string;
  tcComponentInfoFabricWeightOfFinishedProduct: string;
  tcComponentNetShippingWeight: number | null;
  tcComponentSupplementaryWeight: number | null;
  tcComponentInfoAdditional: string;
}

interface ComponentInfoProps {
  formRef: React.Ref<any>;
  componentData: TCComponentInformation;
  setComponentData: (data: TCComponentInformation) => void;
}

const ComponentInfo: React.FC<ComponentInfoProps> = ({ formRef, componentData, setComponentData }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editInitialValues, setEditInitialValues] = useState<TCComponentInformation>(componentData);
  const [productOptions, setProductOptions] = useState<Option[]>([]);
  const { loading, error, executeGet } = useApi();
  const { TE_ID } = useParams()
  const toggleDrawer = () => {
    if (drawerOpen) resetForm();
    setDrawerOpen((prev) => !prev);
  };
  const fetchProductDetails = async () => {
    try {
      const response = await executeGet(TCAppConfig.GET_PRODUCT_RAWMATERIAL_INFO_LIST, { tcId: TE_ID });
      console.log(response?.data)
      const result = (response?.data.response as ProductShipmentResponse[])?.map((detail) => {console.log(detail); return({
        value: detail.tcProductNo,
        label: detail.tcProductNo,
      })}) || [];
      const uniqueData = result.filter((item, index, self) =>
        index === self.findIndex((t) => t.value === item.value)
      );
      setProductOptions(uniqueData)
      //  const result=payloadToMap(response);
      //  setData(result.data);
    } catch (err) {
      console.error('Failed to fetch TC Base Details:', err);
    }
  }
  useEffect(() => {
    TE_ID && fetchProductDetails()
  }, [])

  const resetForm = () => {
    setEditInitialValues(componentData);
  };

  const handleSubmit = (values: TCComponentInformation) => {
    setComponentData(values);
    toggleDrawer();
  };
  const validationSchema = Yup.object().shape({
    tcAssociatedProductNo: Yup.string().required("Please pick a Associated Product No."),
    tcProductComponentNo: Yup.string().nullable(),
    tcComponentDetail: Yup.string().nullable(),
    tcComponentInfoTrimsExcluded: Yup.string().nullable(),
    tcComponentInfoAssociatedTc: Yup.string().nullable(),
    tcComponentInfoFiberLength: Yup.string().nullable(),
    tcComponentInfoYarnCount: Yup.string().nullable(),
    tcComponentInfoFabricWeightAndConstruct: Yup.string().nullable(),
    tcComponentInfoFabricWeightOfFinishedProduct: Yup.string().nullable(),
    tcComponentNetShippingWeight: Yup.string()
      .nullable()
      .test(
        'is-exactly-two-decimal-places',
        'Number must be a valid decimal number with upto 2 decimal places.',
        (value: string | null | undefined) => {
          if (!value || value === null || value === '') return true; // Allow empty values and null for required validation to handle
          return /^(0|[1-9]\d*|0\.\d{1,2}|[1-9]\d*\.\d{1,2})$/.test(value); // Match exactly two decimal places
        }
      )
      .typeError("Component Net Shipping Weight must be a valid number"),
    tcComponentSupplementaryWeight: Yup.string()
      .nullable()
      .test(
        'is-exactly-two-decimal-places',
        'Number must be a valid decimal number with upto 2 decimal places.',
        (value: string | null | undefined) => {
          if (!value || value === null || value === '') return true; // Allow empty values and null for required validation to handle
          return /^(0|[1-9]\d*|0\.\d{1,2}|[1-9]\d*\.\d{1,2})$/.test(value); // Match exactly two decimal places
        }
      )
      .typeError("Component Supplementary Weight must be a valid number"),
    tcComponentInfoAdditional: Yup.string().nullable(),
  });

  const formConfig = {
    // pageName: "component-details",
    initialValues: componentData,
    validationSchema: validationSchema,
    sections: [
      {
        fields: [
          {
            label: 'Associated Product No.',
            name: 'tcAssociatedProductNo',
            type: 'select',
            gridSize: { xs: 12, md: 4 },
            required: false,
            value: componentData.tcAssociatedProductNo,
            props: {
              options: productOptions,
              enableSearch:true,
              placeholder: 'Select Product No.',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component No.',//renamed to match table hdr
            name: 'tcProductComponentNo',
            type: 'text',
            gridSize: { xs: 12, md: 4 },
            required: false,
            value: componentData.tcProductComponentNo,
            props: {
              placeholder: 'Enter Component No.',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Detail',
            name: 'tcComponentDetail',
            type: 'text',
            gridSize: { xs: 12, md: 4 },
            required: false,
            value: componentData.tcComponentDetail,
            props: {
              placeholder: 'Enter Component Detail',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Trims Excluded',
            name: 'tcComponentInfoTrimsExcluded',
            type: 'text',
            gridSize: { xs: 12, md: 4 },
            required: false,
            value: componentData.tcComponentInfoTrimsExcluded,
            props: {
              placeholder: 'Enter Trims Excluded Info',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Associated TC',
            name: 'tcComponentInfoAssociatedTc',
            type: 'text',
            gridSize: { xs: 12, md: 4 },
            required: false,
            value: componentData.tcComponentInfoAssociatedTc,
            props: {
              placeholder: 'Enter Associated TC',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Fiber Length',
            name: 'tcComponentInfoFiberLength',
            type: 'text',
            gridSize: { xs: 12, md: 4 },
            required: false,
            value: componentData.tcComponentInfoFiberLength,
            props: {
              placeholder: 'Enter Fiber Length',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Yarn Count',
            name: 'tcComponentInfoYarnCount',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
            required: false,
            value: componentData.tcComponentInfoYarnCount,
            props: {
              placeholder: 'Enter Yarn Count',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Fabric Weight And Construct (kg)',
            name: 'tcComponentInfoFabricWeightAndConstruct',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
            required: false,
            value: componentData.tcComponentInfoFabricWeightAndConstruct,
            props: {
              placeholder: 'Enter Fabric Weight and Construct',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Fabric Weight Of Finished Product (kg)',
            name: 'tcComponentInfoFabricWeightOfFinishedProduct',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
            required: false,
            value: componentData.tcComponentInfoFabricWeightOfFinishedProduct,
            props: {
              placeholder: 'Enter Fabric Weight of Finished Product',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Net Shipping Weight (kg)',
            name: 'tcComponentNetShippingWeight',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
            required: false,
            value: componentData.tcComponentNetShippingWeight,
            props: {
              type: 'number',
              placeholder: 'Enter Net Shipping Weight (kg)',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Supplementary Weight (kg)',
            name: 'tcComponentSupplementaryWeight',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
            required: false,
            value: componentData.tcComponentSupplementaryWeight,
            props: {
              type: 'number',
              placeholder: 'Enter Supplementary Weight (kg)',
              variant: 'outlined',
              fullWidth: true,
            },
          },
          {
            label: 'Component Info Additional',
            name: 'tcComponentInfoAdditional',
            type: 'text',
            gridSize: { xs: 12, md: 6 },
            required: false,
            value: componentData.tcComponentInfoAdditional,
            props: {
              placeholder: 'Enter Additional Info',
              variant: 'outlined',
              fullWidth: true,
            },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      console.log(values,"values")
      setComponentData(values);
      toggleDrawer();
      // resetForm();
    },
  };

  return (
    <>
      <FormBuilder formConfig={formConfig} onSubmit={formConfig.handleSubmit} formRef={formRef} />
    </>
  );
};

export default ComponentInfo;
